import { useState } from 'react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { LoadingState, EmptyState, ErrorState } from '@/components/ui/States';
import { useResultQueue, useLabDepartments, useCriticalResults, useAcknowledgeCritical } from './hooks';
import { LabStatusBadge, OutsideLabBadge, PriorityBadge, fmtRelative } from './ui';
import ResultEntry from './ResultEntry';
import OutsideLabItemDialog from './OutsideLabItemDialog';
import type { CriticalResult } from './types';

/**
 * The bench: result entry, verification, criticals and amendments.
 *
 * Entry and verification are separate QUEUES, not one list with two buttons,
 * because they are separate jobs done by separate people — which is the
 * arrangement `lab:result-entry` and `lab:verify` exist to enforce. Seeing
 * "what I have to type" and "what I have to sign off" as one list is how they
 * get treated as one job.
 */

const QUEUES = [
  { id: 'pending',  label: 'Result entry' },
  { id: 'entered',  label: 'Verification pending' },
  { id: 'verified', label: 'Verified' },
  { id: 'reported', label: 'Reported' },
] as const;

type QueueId = (typeof QUEUES)[number]['id'];

export interface ResultsTabProps {
  initialFilter?: Record<string, string>;
}

export default function ResultsTab({ initialFilter }: ResultsTabProps) {
  const [queue, setQueue] = useState<QueueId>((initialFilter?.queue as QueueId) ?? 'pending');
  const [criticalOnly, setCriticalOnly] = useState(initialFilter?.critical === '1');
  const [departmentId, setDepartmentId] = useState<number | ''>('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [openItem, setOpenItem] = useState<{ itemId: number; orderId: number } | null>(null);
  const [openOutside, setOpenOutside] = useState<number | null>(null);

  const departments = useLabDepartments();
  const { data, isLoading, error, refetch, isFetching } = useResultQueue({
    queue: criticalOnly ? undefined : queue,
    critical: criticalOnly || undefined,
    department_id: departmentId || undefined,
    q: q.trim() || undefined,
    page,
  });

  const rows = data?.data ?? [];

  return (
    <div className="space-y-3">
      <CriticalStrip />

      <div className="flex flex-wrap items-center gap-2">
        {QUEUES.map((qq) => (
          <button
            key={qq.id}
            type="button"
            onClick={() => { setQueue(qq.id); setCriticalOnly(false); setPage(1); }}
            className={`rounded-full px-3 py-1 text-[12px] ${
              !criticalOnly && queue === qq.id ? 'bg-teal text-white font-semibold' : 'border border-border hover:bg-muted'
            }`}
          >
            {qq.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => { setCriticalOnly(true); setPage(1); }}
          className={`rounded-full px-3 py-1 text-[12px] ${
            criticalOnly ? 'bg-danger text-white font-semibold' : 'border border-border hover:bg-muted'
          }`}
        >
          Critical results
        </button>

        <select className="ml-auto h-9 rounded-lg border border-border bg-card px-2 text-[13px]"
          value={departmentId} onChange={(e) => { setDepartmentId(e.target.value ? Number(e.target.value) : ''); setPage(1); }}>
          <option value="">All departments</option>
          {(departments.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <Input className="w-[220px]" placeholder="Patient, UHID or order…" value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }} />
      </div>

      {isLoading && <LoadingState label="Loading the work list…" />}
      {error && <ErrorState message={(error as Error).message} onRetry={() => refetch()} />}
      {!isLoading && !error && rows.length === 0 && (
        <EmptyState label="Nothing in this queue." hint="Tests appear here once their sample has been accepted." />
      )}

      {rows.length > 0 && (
        <>
          <div className="rounded-xl border border-border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient</TableHead>
                  <TableHead>Test</TableHead>
                  <TableHead>Sample</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const overdue = r.due_at && new Date(r.due_at) < new Date()
                    && !['VERIFIED', 'REPORTED'].includes(r.status);
                  // An outside test verifies against its attached report, so it
                  // opens the outside-lab view rather than the bench entry sheet.
                  const open = () => (r.processing_type === 'outsourced'
                    ? setOpenOutside(r.order_item_id)
                    : setOpenItem({ itemId: r.order_item_id, orderId: r.order_id }));
                  return (
                    <TableRow key={r.order_item_id} className="cursor-pointer" onClick={open}>
                      <TableCell>
                        <div className="font-medium">{r.patient_name}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {r.uhid} · {r.order_number} <PriorityBadge priority={r.priority} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>{r.test_name}<OutsideLabBadge processingType={r.processing_type} referenceLabName={r.reference_lab_name} /></div>
                        <div className="text-[11px] text-muted-foreground">{r.department_name ?? '—'}</div>
                      </TableCell>
                      <TableCell className="text-[11px] text-muted-foreground">
                        {r.barcode && <div className="font-mono">{r.barcode}</div>}
                        <div>Received {r.received_at ? fmtRelative(r.received_at) : '—'}</div>
                      </TableCell>
                      <TableCell className={`text-[11px] ${overdue ? 'font-semibold text-danger-ink' : 'text-muted-foreground'}`}>
                        {r.due_at ? fmtRelative(r.due_at) : '—'}
                        {overdue && <div>past turnaround</div>}
                      </TableCell>
                      <TableCell>
                        <LabStatusBadge status={r.status} />
                        {r.has_critical && <Badge variant="danger" className="ml-1">Critical</Badge>}
                        {!r.has_critical && r.has_abnormal && <Badge variant="warn" className="ml-1">Abnormal</Badge>}
                        {r.sent_back_reason && <Badge variant="warn" className="ml-1">Sent back</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="link" size="sm"
                          onClick={(e) => { e.stopPropagation(); open(); }}>
                          {r.status === 'RESULT_ENTERED' ? 'Verify' : r.status === 'VERIFIED' || r.status === 'REPORTED' ? 'View' : 'Enter'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-[12px] text-muted-foreground">
            <span>{data?.total ?? rows.length} test{(data?.total ?? rows.length) === 1 ? '' : 's'}{isFetching && ' · refreshing…'}</span>
            <span className="flex items-center gap-2">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span>Page {page}</span>
              <Button variant="ghost" size="sm" disabled={!data?.has_more} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </span>
          </div>
        </>
      )}

      <ResultEntry
        orderItemId={openItem?.itemId ?? null}
        orderId={openItem?.orderId}
        onClose={() => setOpenItem(null)}
      />
      <OutsideLabItemDialog orderItemId={openOutside} onClose={() => setOpenOutside(null)} />
    </div>
  );
}

/**
 * Unacknowledged critical values, above everything else on the bench.
 *
 * The acknowledgement is a deliberate act by a named person with an optional
 * note, because that read-back is what accreditation actually asks the
 * hospital to evidence — a dismissed banner is not an acknowledgement.
 */
function CriticalStrip() {
  const { data } = useCriticalResults({ outstanding: true });
  const ack = useAcknowledgeCritical();
  const [target, setTarget] = useState<CriticalResult | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!data || data.length === 0) return null;

  return (
    <>
      <div className="rounded-xl border border-danger-soft bg-danger-soft p-3">
        <div className="mb-1.5 text-sm font-semibold text-danger-ink">
          {data.length} critical result{data.length === 1 ? '' : 's'} awaiting acknowledgement
        </div>
        <div className="space-y-1.5">
          {data.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-card px-3 py-2 text-[12px]">
              <div>
                <span className="font-semibold">{c.patient_name}</span>
                <span className="text-muted-foreground"> · {c.uhid} · {c.order_number}</span>
                <div>
                  {c.test_name} — {c.parameter_name}{' '}
                  <span className="font-semibold text-danger-ink">{c.value_display}</span>
                  <span className="text-muted-foreground">
                    {' '}(critical {c.flag === 'CRITICAL_LOW' ? 'low' : 'high'}
                    {c.threshold_low ? `, below ${Number(c.threshold_low)}` : ''}
                    {c.threshold_high ? `, above ${Number(c.threshold_high)}` : ''})
                  </span>
                </div>
                <div className="text-muted-foreground">
                  Detected {fmtRelative(c.detected_at)} ·{' '}
                  {c.notification_status === 'failed'
                    ? <span className="font-semibold text-danger-ink">not delivered</span>
                    : `notified ${c.responsible_doctor_name ?? 'the responsible doctor'}`}
                  {c.notifications?.[0]?.failure_reason ? ` — ${c.notifications[0].failure_reason}` : ''}
                </div>
              </div>
              <Button size="sm" onClick={() => { setTarget(c); setNotes(''); setError(null); }}>
                Acknowledge
              </Button>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={target !== null} onOpenChange={(o) => { if (!o) setTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Acknowledge critical result</DialogTitle></DialogHeader>
          {target && (
            <div className="space-y-2 text-[13px]">
              <div>
                <span className="font-semibold">{target.patient_name}</span> — {target.test_name},{' '}
                {target.parameter_name} <span className="font-semibold text-danger-ink">{target.value_display}</span>
              </div>
              <p className="text-[12px] text-muted-foreground">
                Your name and the time are recorded against this acknowledgement, alongside the notification
                that was sent. Note what was done about it if anything was.
              </p>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Called Dr Menon at 14:20, read back, transfusion ordered" />
              {error && <ErrorState message={error} />}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTarget(null)}>Cancel</Button>
            <Button
              disabled={ack.isPending}
              onClick={async () => {
                if (!target) return;
                setError(null);
                try {
                  await ack.mutateAsync({ id: target.id, notes: notes.trim() || undefined });
                  setTarget(null);
                } catch (e) { setError((e as Error).message); }
              }}
            >
              {ack.isPending ? 'Recording…' : 'Acknowledge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

